import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { useUpdateWidgetSettings } from '@/Domain/hooks/Widget/useUpdateWidgetSettings.hook';
import { PreChatSection } from '@/UI/Website/Appearance/PreChat.accordion';
import { TextContentSection } from '@/UI/Website/Appearance/TextContent.accordion';

const DEFAULT_PRECHAT_MESSAGE =
  '<h1 class="font-bold">👋 Welcome to our chat!</h1><p class="leading-none my-[10px]">---</p><p class="leading-none my-[10px]">We\'re here to assist you!</p><p class="leading-none my-[10px]">Click the button below to start a conversation and get the help you need.</p><h2 class="font-bold">🚀 How it works:</h2><p class="leading-none my-[10px]">- Ask any question, and we\'ll do our best to assist you.</p><p class="leading-none my-[10px]">- Get quick responses and helpful information.</p><p class="leading-none my-[10px]">- Enjoy a smooth and friendly chat experience.</p><p class="leading-none my-[10px]">---</p><p class="leading-none my-[10px]">👇 Click the button below to begin!</p>';

type Props = {
  data: WidgetSettingsDto;
};

const SectionHeader = ({ label, testId }: { label: string; testId?: string }) => (
  <p
    data-testid={testId}
    className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle"
  >
    {label}
  </p>
);

export const TextContentPage = ({ data }: Props) => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const { reset } = usePreview();

  const form = useForm<WidgetSettingsDto>({
    values: {
      ...data,
      conversation_starters: data.conversation_starters ?? [],
      background_disclaimer_text:
        data.background_disclaimer_text ?? t('background_disclaimer_text.placeholder'),
      prechat_header_text: data.prechat_header_text ?? '',
      prechat_button_text: data.prechat_button_text ?? 'Start Chatbot',
      prechat_message_text: data.prechat_message_text ?? DEFAULT_PRECHAT_MESSAGE,
    },
  });

  useUpdateWidgetSettings({ form, reset });

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-6">
        <SectionHeader label={t('widget:text_content')} testId="section-text-content" />
        <TextContentSection />

        <Separator />

        <section>
          <SectionHeader label={t('widget:prechat')} testId="section-prechat" />
          <PreChatSection />
        </section>
      </form>
    </FormProvider>
  );
};
